import { useUploadProgress } from '../context/UploadProgressContext';
import {
  sendHAReportWithProgress,
  sendPhotosWithProgress,
  sendAnnexPhotosWithProgress,
} from '../utils/UploadWithProgress';
import { toast } from 'react-toastify';

export const useHAReportUpload = () => {
  const { startUpload, updateProgress, finishUpload, setError } = useUploadProgress();

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const simulateStepProgress = async (step, stepName, totalSteps, basePercentage = 0) => {
    const stepRange = 100 / totalSteps;
    const stepStart = (step - 1) * stepRange + basePercentage;

    updateProgress(step, stepName, Math.round(stepStart + 5));
    await delay(150);

    updateProgress(step, stepName, Math.round(stepStart + 15));
    await delay(200);

    updateProgress(step, stepName, Math.round(stepStart + 25));
    await delay(150);
  };

  const uploadHAReport = async (formData, photos, annexImages, options = {}) => {
    const { onSuccess, onError, skipPhotos = false, skipAnnexPhotos = false } = options;

    try {
      let totalSteps = 1;
      if (!skipPhotos && photos.length > 0) totalSteps++;
      if (!skipAnnexPhotos && annexImages.length > 0) totalSteps++;

      startUpload(totalSteps, 'Saving HA report...');

      let step = 0;
      let resultData = null;

      step++;
      await simulateStepProgress(step, 'Saving HA report data...', totalSteps);

      const response = await sendHAReportWithProgress(formData, (percentage) => {
        const stepRange = 100 / totalSteps;
        const stepStart = (step - 1) * stepRange;
        const stepProgress = Math.min(stepStart + (percentage * stepRange) / 100, stepRange * step);
        updateProgress(step, 'Saving HA report data...', Math.round(stepProgress));
      });

      if (response.status === 200) {
        resultData = response.data;
        const stepComplete = Math.round((step / totalSteps) * 100);
        updateProgress(step, 'HA report data saved', stepComplete);
      }

      if (!skipPhotos && photos.length > 0) {
        step++;
        await simulateStepProgress(step, `Uploading ${photos.length} photos...`, totalSteps);

        await sendPhotosWithProgress(resultData.id, photos, (percentage) => {
          const stepRange = 100 / totalSteps;
          const stepStartPos = (step - 1) * stepRange;
          const stepProgress = Math.min(
            stepStartPos + (percentage * stepRange) / 100,
            stepRange * step
          );
          updateProgress(step, `Uploading photos...`, Math.round(stepProgress));
        });

        const stepComplete = Math.round((step / totalSteps) * 100);
        updateProgress(step, 'Photos uploaded', stepComplete);
      }

      if (!skipAnnexPhotos && annexImages.length > 0) {
        step++;
        await simulateStepProgress(
          step,
          `Uploading ${annexImages.length} annex maps...`,
          totalSteps
        );

        await sendAnnexPhotosWithProgress(resultData.id, annexImages, (percentage) => {
          const stepRange = 100 / totalSteps;
          const stepStartPos = (step - 1) * stepRange;
          const stepProgress = Math.min(
            stepStartPos + (percentage * stepRange) / 100,
            stepRange * step
          );
          updateProgress(step, `Uploading annex maps...`, Math.round(stepProgress));
        });

        const stepComplete = Math.round((step / totalSteps) * 100);
        updateProgress(step, 'Annex maps uploaded', stepComplete);
      }

      updateProgress(step, 'Completing...', 95);
      await delay(300);
      updateProgress(step, 'Completed', 100);

      finishUpload();

      const isDraft = formData.draft;
      toast.success(isDraft ? 'HA report drafted' : 'HA report was sent');

      if (onSuccess) {
        onSuccess(resultData);
      }

      return resultData;
    } catch (error) {
      console.error('❌ Upload failed:', error);
      let errorMessage = 'Failed to upload HA report';

      if (error.response?.status === 400) {
        const data = error.response?.data;
        const schemaVersionError = data?.info?._schema_version?.value
          || data?.general?._schema_version?.value;
        if (schemaVersionError) {
          errorMessage = 'This report is read-only and cannot be edited.';
        } else {
          const serverMessage = typeof data === 'object' && data !== null
            ? Object.values(data).flatMap((v) => (typeof v === 'object' && v?.value ? [v.value] : [])).join('; ')
            : null;
          errorMessage = serverMessage || 'Validation error. Please check your data.';
        }
      } else if (error.response?.status === 413) {
        errorMessage = 'Files are too large. Please reduce file sizes.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
      toast.error(errorMessage);

      if (onError) {
        onError(error);
      }

      throw error;
    }
  };

  return {
    uploadHAReport,
  };
};
